import type { CSSProperties, ReactNode } from "react";
import { Sliders, Phone, Sunrise } from "lucide-react";
import type { RedactionLevel, GreetingEnergy } from "@zoidberg/contract";
import { T } from "../theme";
import { Eyebrow, Card } from "../components";
import type { ProfileConfig } from "../api/config";
import { buildPayload } from "../lib/payload";
import { instantTo12h, minutesLabel, hhmmTo12h } from "../lib/format";

/**
 * CONFIG — the P0 config surface (§11): phone numbers, routine durations,
 * empty-morning wake, redaction, persona. The preview at the top recomputes the
 * wake time LIVE through the real solver on every edit — drag the routine slider
 * and watch T₀ move. That's "computed never means opaque" made tangible.
 */
export function Config({
  config,
  setConfig,
}: {
  config: ProfileConfig;
  setConfig: (c: ProfileConfig) => void;
}) {
  // Live recompute — runs the actual @zoidberg/brain compiler in the browser.
  let wakeLabel = { h: "—", ampm: "" };
  let explanation = "";
  let phoneValid = /^\+\d{7,15}$/.test(config.phones.primary);
  try {
    const today = new Intl.DateTimeFormat("en-CA", {
      timeZone: config.timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    const p = buildPayload(config, [], today);
    wakeLabel = instantTo12h(p.wake.wakeTime, config.timezone);
    explanation = p.wake.wakeExplanation;
  } catch {
    explanation = phoneValid ? "—" : "fix the phone number to preview";
  }

  const runway = config.routine.comfortMin - config.routine.minimumMin;

  return (
    <div style={{ padding: "18px 16px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Eyebrow>configuration</Eyebrow>
        <Sliders size={14} color={T.mist} />
      </div>

      {/* LIVE preview */}
      <Card style={{ marginTop: 14, borderColor: "rgba(255,180,84,.4)", textAlign: "center", padding: "16px 14px" }}>
        <Eyebrow color={T.gold}>computed wake · against the sample day</Eyebrow>
        <div className="mono" style={{ fontSize: 40, fontWeight: 300, color: T.paper, lineHeight: 1.1, marginTop: 4 }}>
          {wakeLabel.h}
          <span style={{ fontSize: 16, color: T.gold }}> {wakeLabel.ampm}</span>
        </div>
        <div className="mono" style={{ fontSize: 9.5, color: T.dim, marginTop: 6, letterSpacing: "0.03em" }}>
          {explanation}
        </div>
      </Card>

      {/* Phone numbers */}
      <Section icon={<Phone size={12} color={T.mist} />} title="phone numbers">
        <FieldLabel>primary · E.164</FieldLabel>
        <input
          value={config.phones.primary}
          onChange={(e) => setConfig({ ...config, phones: { ...config.phones, primary: e.target.value } })}
          placeholder="+13055550100"
          style={inputStyle(phoneValid)}
        />
        {!phoneValid && (
          <div className="mono" style={{ fontSize: 9, color: T.dawn, marginTop: 4 }}>
            must look like +1 305 555 0100
          </div>
        )}
      </Section>

      {/* Routine durations */}
      <Section icon={<Sunrise size={12} color={T.mist} />} title="morning routine">
        <Slider
          label="comfortable"
          value={config.routine.comfortMin}
          min={45}
          max={150}
          step={5}
          display={minutesLabel(config.routine.comfortMin)}
          onChange={(v) => setConfig({ ...config, routine: { ...config.routine, comfortMin: v } })}
        />
        <Slider
          label="hard-wake minimum"
          value={config.routine.minimumMin}
          min={30}
          max={120}
          step={5}
          display={minutesLabel(config.routine.minimumMin)}
          onChange={(v) => setConfig({ ...config, routine: { ...config.routine, minimumMin: v } })}
        />
        <div className="mono" style={{ fontSize: 9.5, color: runway > 0 ? T.mint : T.dawn, marginTop: 6 }}>
          {runway > 0
            ? `${runway}m escalation runway between wake and hard wake`
            : "minimum must be below comfortable"}
        </div>
      </Section>

      {/* Empty-morning */}
      <Section title="empty morning">
        <Slider
          label="fallback wake (no sleep data)"
          value={config.emptyMorning.fallbackMin}
          min={6 * 60}
          max={12 * 60}
          step={15}
          display={hhmmTo12h(toHHMM(config.emptyMorning.fallbackMin))}
          onChange={(v) => setConfig({ ...config, emptyMorning: { ...config.emptyMorning, fallbackMin: v } })}
        />
        <Slider
          label="latest start (clamp)"
          value={config.emptyMorning.latestMin}
          min={9 * 60}
          max={13 * 60}
          step={15}
          display={hhmmTo12h(toHHMM(config.emptyMorning.latestMin))}
          onChange={(v) => setConfig({ ...config, emptyMorning: { ...config.emptyMorning, latestMin: v } })}
        />
      </Section>

      {/* Redaction */}
      <Section title="what calls say out loud">
        <Segmented<RedactionLevel>
          options={[
            ["open", "Open"],
            ["initials", "Initials"],
            ["opaque", "Opaque"],
          ]}
          value={config.redactionLevel}
          onChange={(v) => setConfig({ ...config, redactionLevel: v })}
        />
      </Section>

      {/* Persona */}
      <Section title="voice">
        <FieldLabel>persona label</FieldLabel>
        <input
          value={config.persona.voiceLabel}
          onChange={(e) => setConfig({ ...config, persona: { ...config.persona, voiceLabel: e.target.value } })}
          style={inputStyle(true)}
        />
        <div style={{ marginTop: 10 }}>
          <Slider
            label="speed"
            value={Math.round(config.persona.speed * 100)}
            min={70}
            max={130}
            step={5}
            display={`${config.persona.speed.toFixed(2)}×`}
            onChange={(v) => setConfig({ ...config, persona: { ...config.persona, speed: v / 100 } })}
          />
        </div>
        <div style={{ marginTop: 10 }}>
          <FieldLabel>greeting energy</FieldLabel>
          <Segmented<GreetingEnergy>
            options={[
              ["gentle", "Gentle"],
              ["neutral", "Neutral"],
              ["firm", "Firm"],
            ]}
            value={config.persona.greetingEnergy}
            onChange={(v) => setConfig({ ...config, persona: { ...config.persona, greetingEnergy: v } })}
          />
        </div>
      </Section>

      <div className="mono" style={{ fontSize: 9, color: T.dim, marginTop: 16, textAlign: "center" }}>
        saved locally · syncs to the brain when connected
      </div>
    </div>
  );
}

function toHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

/* ── form bits ─────────────────────────────── */

function Section({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        {icon}
        <Eyebrow>{title}</Eyebrow>
      </div>
      <Card>{children}</Card>
    </div>
  );
}

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <div className="mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: T.dim, textTransform: "uppercase", marginBottom: 5 }}>
    {children}
  </div>
);

function inputStyle(valid: boolean): CSSProperties {
  return {
    width: "100%",
    background: T.night,
    border: `1px solid ${valid ? T.edge : T.dawn}`,
    borderRadius: 9,
    padding: "9px 11px",
    color: T.paper,
    fontSize: 13,
    outline: "none",
  };
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (v: number) => void;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ fontSize: 12, color: T.mist }}>{label}</span>
        <span className="mono" style={{ fontSize: 12, color: T.gold, fontWeight: 600 }}>{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: T.gold, marginTop: 6 }}
      />
    </div>
  );
}

function Segmented<V extends string>({
  options,
  value,
  onChange,
}: {
  options: [V, string][];
  value: V;
  onChange: (v: V) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {options.map(([v, label]) => {
        const active = v === value;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              flex: 1,
              textAlign: "center",
              padding: "8px 0",
              borderRadius: 10,
              fontSize: 11.5,
              fontWeight: 600,
              cursor: "pointer",
              background: active ? "rgba(255,180,84,.16)" : T.panel,
              border: `1px solid ${active ? T.gold : T.edge}`,
              color: active ? T.gold : T.dim,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
